import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/index.js';
import { authAPI } from './services/api.js';
import './styles/index.css';

const LoginPage = lazy(() => import('./pages/LoginPage.jsx'));
const Layout = lazy(() => import('./pages/Layout.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage.jsx'));
const TemplateEditorPage = lazy(() => import('./pages/TemplateEditorPage.jsx'));
const ShipmentsPage = lazy(() => import('./pages/ShipmentsPage.jsx'));
const LogsPage = lazy(() => import('./pages/LogsPage.jsx'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage.jsx'));
const UsersPage = lazy(() => import('./pages/UsersPage.jsx'));
const MyPlanPage = lazy(() => import('./pages/MyPlanPage.jsx'));
const AdminPlansPage = lazy(() => import('./pages/AdminPlansPage.jsx'));
const ReferencesPage = lazy(() => import('./pages/ReferencesPage.jsx'));

const ProtectedRoute = ({ element }) => {
  const token = useAuthStore(state => state.token);
  return token ? element : <Navigate to="/login" replace />;
};

function App() {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  useEffect(() => {
    const hydrateUser = async () => {
      if (!token || user) return;
      try {
        const res = await authAPI.getProfile();
        setUser({
          id: res.data.id,
          email: res.data.email,
          fullName: res.data.full_name,
          isAdmin: Boolean(res.data.is_admin),
          plan: res.data.plan || 'free',
          canCreateTemplates: res.data.can_create_templates !== false,
          canEditTemplates: res.data.can_edit_templates !== false,
          canDeleteTemplates: res.data.can_delete_templates !== false,
          canDeleteLogs: res.data.can_delete_logs !== false,
          canViewIntegrations: res.data.can_view_integrations !== false,
          canManageUsers: res.data.can_manage_users === true
        });
      } catch {
        // 401 is handled by the API interceptor.
      }
    };

    hydrateUser();
  }, [token, user, setUser]);

  return (
    <Router>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-gray-600">Cargando...</div>}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedRoute element={<Layout />} />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/templates/new" element={<TemplateEditorPage />} />
            <Route path="/templates/:id/edit" element={<TemplateEditorPage />} />
            <Route path="/shipments" element={<ShipmentsPage />} />
            <Route path="/logs" element={<LogsPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/my-plan" element={<MyPlanPage />} />
            <Route path="/admin/plans" element={<AdminPlansPage />} />
            <Route path="/references" element={<ReferencesPage />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
